import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Essential Pillars audience picker — the three quick-filters that most
 * research briefs need: AGE, GENDER, GEOGRAPHY.
 *
 * Everything else lives behind the "Advanced targeting" drawer below.
 *
 * Emits into `target_demographics`:
 *   - `cr_quotas[]`      — gender pillar maps to PS qualification_code=211
 *                          (auto-detected from the catalog via desc match).
 *                          Age brackets are also merged in as a synthetic
 *                          quota (id="__age__") so backend feasibility
 *                          shrinkage kicks in — the backend caps unknown IDs
 *                          to the average incidence rate.
 *   - `country`, `region` — geography pillar. Consumed by the existing
 *                          backend feasibility endpoint.
 *
 * The `advancedQuotas` prop is the set of quotas already in the drawer so we
 * don't clobber them when writing pillar quotas.
 */
const AGE_BRACKETS = [
  { key: "18-24", min: 18, max: 24 },
  { key: "25-34", min: 25, max: 34 },
  { key: "35-44", min: 35, max: 44 },
  { key: "45-54", min: 45, max: 54 },
  { key: "55-64", min: 55, max: 64 },
  { key: "65+",   min: 65, max: 99 },
];

const GENDER_OPTIONS = [
  { key: "female", label: "Female", matchers: [/^female$/i] },
  { key: "male",   label: "Male",   matchers: [/^male$/i] },
  { key: "nonbinary", label: "Non-binary / Prefer not to say", matchers: [/non.?binary/i, /prefer not/i, /other/i] },
];

/**
 * Extra pillars beyond Age/Gender/Geography — each maps to a canonical
 * PureSpectrum qualification code discovered in the staging catalog. Codes
 * are hardcoded as a fast-path so the pillar renders instantly even before
 * the (paginated) demographics catalog finishes loading; when the catalog
 * arrives we cross-check the actual `demographicId` at render time and
 * hot-swap if PS ever reassigns the id.
 *
 *   Language      → 1511 (12 options)
 *   Income (HH)   → 1101 (12 options)
 *   Relationship  → 3195 (4 options)
 *   Employment    →  215 (5 options)
 *   Education     →  216 (6 options)
 *   Device        →  219 (3 options)
 */
const EXTRA_PILLARS = [
  { key: "language",     title: "Language",     hint: "Multi-select · reads comprehensively",
    fastPathId: "1511", matcher: /which of the following languages can you read/i, style: "menu" },
  { key: "income",       title: "Household income", hint: "Multi-select · annual USD ranges",
    fastPathId: "1101", matcher: /pick the range that best describes.*total annual income/i, style: "menu" },
  { key: "relationship", title: "Relationship",  hint: "Marital / partnership status",
    fastPathId: "3195", matcher: /what is your marital status/i, style: "chip" },
  { key: "employment",   title: "Employment",   hint: "Working status",
    fastPathId: "215",  matcher: /i['']?m employed|employment status/i, style: "chip" },
  { key: "education",    title: "Education",    hint: "Highest level completed",
    fastPathId: "216",  matcher: /highest level of education/i, style: "chip" },
  { key: "device",       title: "Device",       hint: "Primary survey device",
    fastPathId: "219",  matcher: /^device$/i, style: "chip" },
];

// The dynamic list of PS-supported countries is now fetched at runtime from
// GET /api/campaigns/locales (grouped by region) so this component stays a
// single source of truth for the marketplace catalog. See CountrySelect below.

// The synthetic quota row we write for the AGE pillar. The backend catalog
// won't have this id → its shrinkage falls back to the mean-incidence default,
// which is exactly what we want (an age bracket ≈ typical incidence).
const AGE_SYNTHETIC_ID = "__age__";

/** Discover the PS demographic ID for a keyword from the fetched catalog. */
function findDemographicByQuestion(demographicsData, matcher) {
  if (!demographicsData?.flat) return null;
  return demographicsData.flat.find((d) => matcher.test((d.question || "")));
}

/** Discover which option ids on a demographic match a set of label matchers. */
function matchOptionIds(demographic, matchers) {
  if (!demographic?.options) return [];
  const hits = [];
  for (const opt of demographic.options) {
    for (const rx of matchers) {
      if (rx.test((opt.value || ""))) {
        hits.push(String(opt.demographicOptionId));
        break;
      }
    }
  }
  return hits;
}

function upsertQuota(quotas, demographicId, optionIds) {
  const next = (quotas || []).filter((q) => q.demographicId !== demographicId);
  if (optionIds && optionIds.length > 0) {
    next.push({ demographicId, demographicOptionIds: [...optionIds] });
  }
  return next;
}

export default function PillarsAudience({
  demographicsData,
  demographicsLoading,
  target_demographics,
  onChange,
  testid = "pillars-audience",
}) {
  // Discover the "Gender" demographic from the live PS catalog (id=211 in
  // staging). The `^I'?m a` matches "I'm a"; fall back to a plain /gender/i
  // scan if the tenant renamed it.
  const genderDemo = useMemo(() => {
    return (
      findDemographicByQuestion(demographicsData, /^I['']?m a\b/i) ||
      demographicsData?.flat?.find((d) =>
        /gender/i.test(d.question || "") &&
        !/child|infant|baby/i.test(d.question || "")
      ) ||
      null
    );
  }, [demographicsData]);

  // Cache the PS gender demo id + the union of all 3 gender option ids
  // (Female + Male + Non-binary/PNS/Other) into target_demographics so the
  // feasibility call in CampaignBuilder can inject an "all-genders" quota
  // when the user has picked zero gender chips. Rationale: PS returns the
  // raw panel size (which includes panelists with unknown gender) when no
  // gender criterion is passed, but the buyer's mental model is "eligible
  // = sum of Male + Female + Non-binary". Stashing the id/options here
  // keeps that logic invisible in the persisted quotas but visible to the
  // ring counter.
  useEffect(() => {
    if (!genderDemo) return;
    const allMatchers = GENDER_OPTIONS.flatMap((g) => g.matchers);
    const allIds = matchOptionIds(genderDemo, allMatchers);
    const nextDemoId = String(genderDemo.demographicId);
    const nextIds = allIds.map(String);
    const prevDemoId = target_demographics?.pillar_gender_demo_id || null;
    const prevIds = target_demographics?.pillar_gender_all_ids || [];
    const idsChanged = prevIds.length !== nextIds.length
      || prevIds.some((x, i) => x !== nextIds[i]);
    if (prevDemoId !== nextDemoId || idsChanged) {
      onChange({
        ...target_demographics,
        pillar_gender_demo_id: nextDemoId,
        pillar_gender_all_ids: nextIds,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genderDemo]);

  const quotas = target_demographics?.cr_quotas || [];

  // ============ AGE state ============
  // Age is tracked in `target_demographics.pillar_age` as a set of bracket
  // keys so the UI is fully declarative from parent state.
  const selectedAgeKeys = target_demographics?.pillar_age || [];

  const toggleAge = (bracket) => {
    const next = selectedAgeKeys.includes(bracket.key)
      ? selectedAgeKeys.filter((k) => k !== bracket.key)
      : [...selectedAgeKeys, bracket.key];
    // Also compute the union min..max for downstream backend integration and
    // stash a synthetic quota so feasibility shrinkage kicks in.
    let minAge = null; let maxAge = null;
    for (const k of next) {
      const b = AGE_BRACKETS.find((x) => x.key === k);
      if (!b) continue;
      minAge = minAge === null ? b.min : Math.min(minAge, b.min);
      maxAge = maxAge === null ? b.max : Math.max(maxAge, b.max);
    }
    const synthetic = next.length > 0
      ? [{ demographicId: AGE_SYNTHETIC_ID, demographicOptionIds: next }]
      : [];
    const withoutAge = quotas.filter((q) => q.demographicId !== AGE_SYNTHETIC_ID);
    onChange({
      ...target_demographics,
      pillar_age: next,
      ps_age_min: minAge, ps_age_max: maxAge,
      cr_quotas: [...withoutAge, ...synthetic],
    });
  };

  // ============ GENDER state ============
  const selectedGenderKeys = target_demographics?.pillar_gender || [];

  const toggleGender = (opt) => {
    const nextKeys = selectedGenderKeys.includes(opt.key)
      ? selectedGenderKeys.filter((k) => k !== opt.key)
      : [...selectedGenderKeys, opt.key];
    // Translate the pillar keys → PS option ids off the live catalog.
    let optionIds = [];
    if (genderDemo) {
      const matchers = [];
      for (const k of nextKeys) {
        const g = GENDER_OPTIONS.find((x) => x.key === k);
        if (g) matchers.push(...g.matchers);
      }
      optionIds = matchOptionIds(genderDemo, matchers);
    }
    const nextQuotas = genderDemo
      ? upsertQuota(quotas, String(genderDemo.demographicId), optionIds)
      : quotas;
    onChange({
      ...target_demographics,
      pillar_gender: nextKeys,
      cr_quotas: nextQuotas,
    });
  };

  // ============ GEOGRAPHY state ============
  const country = target_demographics?.country || "";

  const setCountry = (code) => onChange({
    ...target_demographics,
    country: code,
    // Reset sub-region whenever the country changes to prevent orphaned
    // codes from another country's catalog leaking through.
    subregion: undefined,
  });

  // ============ EXTRA PILLARS (Language / Income / Relationship / Employment / Education / Device) ============
  // Each extra pillar resolves its PS demographic AT RENDER TIME so the
  // options are always fresh from the catalog. We accept both a "fast-path"
  // id (fast for first paint) and a matcher (survives PS ID reassignment).
  const extras = useMemo(() => {
    if (!demographicsData?.flat) return [];
    return EXTRA_PILLARS.map((cfg) => {
      const byId = demographicsData.flat.find(
        (d) => String(d.demographicId) === cfg.fastPathId,
      );
      const byMatch = byId || demographicsData.flat.find(
        (d) => cfg.matcher.test((d.question || "")),
      );
      return { cfg, demo: byMatch || null };
    });
  }, [demographicsData]);

  const upsertExtraQuota = (demoId, optionIds) => {
    onChange({
      ...target_demographics,
      cr_quotas: upsertQuota(quotas, String(demoId), optionIds.map(String)),
    });
  };

  const currentSelection = (demoId) => {
    const q = quotas.find((x) => String(x.demographicId) === String(demoId));
    return (q?.demographicOptionIds || []).map(String);
  };

  const toggleExtraOption = (demoId, optId) => {
    const current = currentSelection(demoId);
    const next = current.includes(String(optId))
      ? current.filter((x) => x !== String(optId))
      : [...current, String(optId)];
    upsertExtraQuota(demoId, next);
  };

  return (
    <div className="space-y-5" data-testid={testid}>
      {/* AGE pillar */}
      <PillarCard
        title="Age"
        hint="Pick one or more brackets"
        testid={`${testid}-age`}
        selectionCount={selectedAgeKeys.length}
      >
        <div className="flex flex-wrap gap-1.5">
          {AGE_BRACKETS.map((b) => (
            <ToggleChip
              key={b.key}
              active={selectedAgeKeys.includes(b.key)}
              onClick={() => toggleAge(b)}
              testid={`${testid}-age-${b.key}`}
            >
              {b.key}
            </ToggleChip>
          ))}
        </div>
      </PillarCard>

      {/* GENDER pillar */}
      <PillarCard
        title="Gender"
        hint={demographicsLoading ? "Loading catalog…" : (genderDemo ? "Multi-select" : "Catalog unavailable")}
        testid={`${testid}-gender`}
        selectionCount={selectedGenderKeys.length}
      >
        <div className="flex flex-wrap gap-1.5">
          {GENDER_OPTIONS.map((g) => (
            <ToggleChip
              key={g.key}
              active={selectedGenderKeys.includes(g.key)}
              onClick={() => toggleGender(g)}
              disabled={!genderDemo}
              testid={`${testid}-gender-${g.key}`}
            >
              {g.label}
            </ToggleChip>
          ))}
        </div>
      </PillarCard>

      {/* GEO pillar */}
      <PillarCard
        title="Geography"
        hint="Country + optional sub-region"
        testid={`${testid}-geo`}
        selectionCount={(country ? 1 : 0) + (target_demographics?.subregion ? 1 : 0)}
      >
        <div className="space-y-3">
          <div className="flex gap-3 flex-wrap items-start">
            <CountrySelect value={country} onChange={setCountry} testid={`${testid}-geo-country`} />
          </div>
          {country && (
            <SubRegionSelect
              country={country}
              value={target_demographics?.subregion || ""}
              onChange={(v) => onChange({ ...target_demographics, subregion: v || undefined })}
              testid={`${testid}-geo-subregion`}
            />
          )}
        </div>
      </PillarCard>

      {/* Extra pillars — Language / Income / Relationship / Employment / Education / Device */}
      {extras.map(({ cfg, demo }) => {
        const opts = demo?.options || [];
        const selected = demo ? currentSelection(demo.demographicId) : [];
        if (!demo && !demographicsLoading) return null;
        return (
          <PillarCard
            key={cfg.key}
            title={cfg.title}
            hint={demographicsLoading && !demo ? "Loading catalog…" : (demo ? cfg.hint : "Unavailable")}
            testid={`${testid}-${cfg.key}`}
            selectionCount={selected.length}
          >
            {cfg.style === "chip" ? (
              <div className="flex flex-wrap gap-1.5">
                {opts.map((o) => (
                  <ToggleChip
                    key={o.demographicOptionId}
                    active={selected.includes(String(o.demographicOptionId))}
                    onClick={() => toggleExtraOption(demo.demographicId, o.demographicOptionId)}
                    disabled={!demo}
                    testid={`${testid}-${cfg.key}-${o.demographicOptionId}`}
                  >
                    {o.value}
                  </ToggleChip>
                ))}
              </div>
            ) : (
              <MultiSelectMenu
                options={opts}
                selected={selected}
                onToggle={(id) => toggleExtraOption(demo.demographicId, id)}
                testid={`${testid}-${cfg.key}`}
                placeholder={`Choose ${cfg.title.toLowerCase()}…`}
              />
            )}
          </PillarCard>
        );
      })}
    </div>
  );
}

/* ==================== presentational helpers ==================== */

function PillarCard({ title, hint, selectionCount, testid, children }) {
  return (
    <div className="border border-[var(--rl-border)] bg-white" data-testid={testid}>
      <div className="flex items-baseline gap-3 px-4 pt-3 pb-2">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text)]">{title}</div>
        {selectionCount > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-[var(--rl-brand)] text-white text-[10px] font-mono font-bold"
            data-testid={`${testid}-count`}
          >
            {selectionCount}
          </span>
        )}
        <span className="ml-auto text-[10px] font-mono text-[var(--rl-text-secondary)]">{hint}</span>
      </div>
      <div className="px-4 pb-4">{children}</div>
    </div>
  );
}

function ToggleChip({ active, onClick, disabled, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "px-3 py-1.5 text-xs font-semibold border transition-all duration-150 " +
        (disabled
          ? "opacity-40 cursor-not-allowed border-[var(--rl-border)] text-[var(--rl-text-secondary)]"
          : active
            ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)] shadow-[2px_2px_0_var(--rl-brand)]"
            : "bg-white text-[var(--rl-text)] border-[var(--rl-border)] hover:border-[var(--rl-text)] hover:-translate-y-px")
      }
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function CountrySelect({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch the PS marketplace catalog once — served by /api/campaigns/locales.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/campaigns/locales");
        if (!cancelled) setCatalog(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || "Failed to load countries");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const current = useMemo(() => {
    if (!catalog?.flat) return null;
    return catalog.flat.find((c) => c.code === value) || null;
  }, [catalog, value]);

  // Filter by name OR code for quick typeahead.
  const filteredByRegion = useMemo(() => {
    if (!catalog?.by_region) return {};
    const q = search.trim().toLowerCase();
    const out = {};
    for (const region of catalog.regions) {
      const rows = (catalog.by_region[region] || []).filter((c) => {
        if (!q) return true;
        return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
      });
      if (rows.length > 0) out[region] = rows;
    }
    return out;
  }, [catalog, search]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest?.(`[data-testid="${testid}"]`)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [testid]);

  return (
    <div className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={loading || !!error}
        className="inline-flex items-center gap-2 min-w-[200px] bg-white border border-[var(--rl-border)] px-3 py-2 text-sm font-semibold hover:border-[var(--rl-text)] transition-colors disabled:opacity-50"
        data-testid={`${testid}-btn`}
      >
        <span className="text-[10px] font-mono text-[var(--rl-text-secondary)]">{current?.code || "—"}</span>
        <span className="flex-1 text-left">
          {loading ? "Loading countries…" : error ? "Catalog unavailable" : (current?.name || "Select country")}
        </span>
        <ChevronDown className={"w-3.5 h-3.5 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && !loading && !error && catalog && (
        <div
          className="absolute left-0 mt-1 min-w-[300px] max-h-[380px] overflow-hidden flex flex-col bg-white border border-[var(--rl-text)] shadow-[4px_4px_0_var(--rl-border)] z-30"
          data-testid={`${testid}-menu`}
        >
          <div className="px-3 pt-2 pb-1.5 border-b border-[var(--rl-border)] bg-[var(--rl-bg-subtle)]">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${catalog.total} countries…`}
              className="w-full bg-white border border-[var(--rl-border)] px-2 py-1.5 text-xs focus:outline-none focus:border-[var(--rl-brand)]"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {Object.keys(filteredByRegion).length === 0 && (
              <div className="px-3 py-4 text-xs text-[var(--rl-text-secondary)] italic" data-testid={`${testid}-empty`}>
                No matches for &ldquo;{search}&rdquo;
              </div>
            )}
            {catalog.regions.filter((r) => filteredByRegion[r]).map((region) => (
              <div key={region}>
                <div className="sticky top-0 bg-white px-3 py-1.5 text-[9px] uppercase tracking-[0.25em] font-bold text-[var(--rl-text-secondary)] border-b border-[var(--rl-border)]">
                  {region}
                </div>
                {filteredByRegion[region].map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => { onChange(c.code); setOpen(false); setSearch(""); }}
                    className={
                      "w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--rl-bg-subtle)] " +
                      (c.code === value ? "bg-[var(--rl-bg-subtle)] font-semibold" : "")
                    }
                    data-testid={`${testid}-opt-${c.code}`}
                  >
                    <span className="text-[10px] font-mono text-[var(--rl-text-secondary)] w-6">{c.code}</span>
                    <span className="flex-1">{c.name}</span>
                    <span className="text-[9px] uppercase tracking-[0.2em] font-mono text-[var(--rl-text-secondary)]">{c.language}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * SubRegionSelect — conditional dropdown that loads a country's curated
 * sub-region catalog (provinces / states / prefectures) from
 * GET /api/campaigns/locales/{country}/subregions.
 *
 * Renders NOTHING when the country isn't in the curated top-10 map so the
 * UI stays clean for markets without a good sub-region taxonomy.
 */
function SubRegionSelect({ country, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refetch whenever the parent country changes.
  useEffect(() => {
    if (!country) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await api.get(`/campaigns/locales/${country}/subregions`);
        if (!cancelled) setPayload(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || "Failed to load sub-regions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [country]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest?.(`[data-testid="${testid}"]`)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [testid]);

  // Country not curated → render nothing per product spec.
  if (payload && !payload.supported) return null;
  if (error) return null;

  const subregions = payload?.subregions || [];
  const current = subregions.find((s) => s.code === value) || null;
  const q = search.trim().toLowerCase();
  const filtered = q
    ? subregions.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q))
    : subregions;

  return (
    <div className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={loading}
        className="inline-flex items-center gap-2 min-w-[240px] bg-white border border-[var(--rl-border)] px-3 py-2 text-sm hover:border-[var(--rl-text)] transition-colors disabled:opacity-50"
        data-testid={`${testid}-btn`}
      >
        <span className="text-[10px] font-mono text-[var(--rl-text-secondary)] w-8 truncate">{current?.code || "SR"}</span>
        <span className="flex-1 text-left">
          {loading
            ? "Loading sub-regions…"
            : (current?.name || `Sub-region (optional)`)}
        </span>
        {current && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear sub-region"
            onClick={(e) => { e.stopPropagation(); onChange(""); setSearch(""); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault(); e.stopPropagation(); onChange(""); setSearch("");
              }
            }}
            className="text-[10px] font-bold text-[var(--rl-text-secondary)] hover:text-[var(--rl-accent)] px-1"
            data-testid={`${testid}-clear`}
          >
            ✕
          </span>
        )}
        <ChevronDown className={"w-3.5 h-3.5 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && !loading && (
        <div
          className="absolute left-0 mt-1 min-w-[280px] max-h-[300px] overflow-hidden flex flex-col bg-white border border-[var(--rl-text)] shadow-[4px_4px_0_var(--rl-border)] z-30"
          data-testid={`${testid}-menu`}
        >
          {subregions.length > 8 && (
            <div className="px-3 pt-2 pb-1.5 border-b border-[var(--rl-border)] bg-[var(--rl-bg-subtle)]">
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${subregions.length} sub-regions…`}
                className="w-full bg-white border border-[var(--rl-border)] px-2 py-1.5 text-xs focus:outline-none focus:border-[var(--rl-brand)]"
                data-testid={`${testid}-search`}
              />
            </div>
          )}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-[var(--rl-text-secondary)] italic" data-testid={`${testid}-empty`}>
                No matches for &ldquo;{search}&rdquo;
              </div>
            )}
            {filtered.map((s) => (
              <button
                key={s.code}
                type="button"
                onClick={() => { onChange(s.code); setOpen(false); setSearch(""); }}
                className={
                  "w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--rl-bg-subtle)] " +
                  (s.code === value ? "bg-[var(--rl-bg-subtle)] font-semibold" : "")
                }
                data-testid={`${testid}-opt-${s.code}`}
              >
                <span className="text-[10px] font-mono text-[var(--rl-text-secondary)] w-10 truncate">{s.code}</span>
                <span className="flex-1 truncate">{s.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * MultiSelectMenu — dropdown with a checkbox list. Used for pillars whose
 * option count exceeds what fits nicely as chips (Language 12, Income 12).
 * Menu shows the count of selected items on the trigger + a preview label
 * of the first selection.
 */
function MultiSelectMenu({ options, selected, onToggle, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest?.(`[data-testid="${testid}-wrap"]`)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [testid]);

  const previewLabel = (() => {
    if (!selected?.length) return placeholder;
    const first = options.find((o) => String(o.demographicOptionId) === selected[0]);
    if (!first) return `${selected.length} selected`;
    if (selected.length === 1) return first.value;
    return `${first.value} + ${selected.length - 1} more`;
  })();

  return (
    <div className="relative inline-block" data-testid={`${testid}-wrap`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 min-w-[280px] max-w-full bg-white border border-[var(--rl-border)] px-3 py-2 text-sm hover:border-[var(--rl-text)] transition-colors"
        data-testid={`${testid}-btn`}
      >
        <span className={"flex-1 text-left truncate " + (selected.length ? "text-[var(--rl-text)] font-semibold" : "text-[var(--rl-text-secondary)]")}>
          {previewLabel}
        </span>
        {selected.length > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-[var(--rl-brand)] text-white text-[10px] font-mono font-bold"
            data-testid={`${testid}-count`}
          >
            {selected.length}
          </span>
        )}
        <ChevronDown className={"w-3.5 h-3.5 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div
          className="absolute left-0 mt-1 min-w-[300px] max-h-[280px] overflow-y-auto bg-white border border-[var(--rl-text)] shadow-[4px_4px_0_var(--rl-border)] z-30"
          data-testid={`${testid}-menu`}
        >
          {options.map((o) => {
            const id = String(o.demographicOptionId);
            const isSel = selected.includes(id);
            return (
              <label
                key={id}
                className={"flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[var(--rl-bg-subtle)] " + (isSel ? "bg-[var(--rl-bg-subtle)]" : "")}
                data-testid={`${testid}-opt-${id}`}
              >
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => onToggle(id)}
                  className="w-3.5 h-3.5 accent-[var(--rl-brand)]"
                />
                <span className="flex-1 truncate">{o.value}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

